<template>
  <div class="MediaPlayerCmp">
    <lightbox-cmp :data-fullscreen="modal">
      <template>
        <youtube-media-player-cmp-extended
          v-if="themeVariant !== 'highlighted'"
          :class="[{ fullPage: fullscreen }]"
          :data-background-image-src="dataBackgroundImageSrc"
          :data-youtube-video-id="dataYoutubeVideoId"
          :data-bootstrap-container-classes="bootstrapContainerClasses"
          :data-bootstrap-row-classes="bootstrapRowClasses"
          :data-bootstrap-col-classes="bootstrapColClasses"
          :data-zoom-in="true"
          @zoom-in="modal = true"
        >
          <template slot="icon-play">
            <svg>
              <use href="#icon-play" />
            </svg>
          </template>
          <template slot="caption">
            <slot />
          </template>
        </youtube-media-player-cmp-extended>
        <highlighted-video-cmp
          v-else
          :data-bootstrap-container-classes="bootstrapContainerClasses"
        >
          <youtube-media-player-cmp-extended
            :data-background-image-src="dataBackgroundImageSrc"
            :data-youtube-video-id="dataYoutubeVideoId"
            :data-zoom-in="true"
            @zoom-in="modal = true"
          >
            <template slot="icon-play">
              <svg>
                <use href="#icon-play" />
              </svg>
            </template>
            <template slot="caption">
              <slot />
            </template>
          </youtube-media-player-cmp-extended>
        </highlighted-video-cmp>
      </template>
      <template slot="lightbox">
        <button type="button" class="zoom-out" @click="onDismiss">
          <svg>
            <use href="#icon-zoom-out" />
          </svg>
        </button>
        <youtube-media-player-cmp-extended
          ref="fullScreenPlayer"
          class="fullscreen-mode"
          :data-dismissible="true"
          :data-background-image-src="dataBackgroundImageSrc"
          :data-youtube-video-id="dataYoutubeVideoId"
          :data-bootstrap-row-classes="['row']"
          :data-bootstrap-col-classes="['col-12']"
          @player-ready="handlePlayerReady"
          @zoom-out="modal = false"
        >
          <template slot="icon-play">
            <svg>
              <use href="#icon-play" />
            </svg>
          </template>
        </youtube-media-player-cmp-extended>
      </template>
    </lightbox-cmp>
  </div>
</template>

<script>
import { LightboxCmp } from '@frankhoodbs/lib';
import HighlightedVideoCmp from './highlighted-video-cmp';
import YoutubeMediaPlayerCmpExtended from './youtube-media-player-cmp-extended';
import { castPropToType } from '@/helpers/cms-support';
import { mdAndUp } from '@/helpers/breakpoints.js';

export default {
  name: 'MediaPlayerCmp',
  components: {
    LightboxCmp,
    HighlightedVideoCmp,
    YoutubeMediaPlayerCmpExtended,
  },
  props: {
    dataBackgroundImageSrc: {
      type: String,
      default: '',
    },
    dataYoutubeVideoId: {
      type: String,
      required: true,
    },
    dataBootstrapContainerClasses: {
      type: [String, Array],
      default: () => ['container'],
    },
    dataBootstrapRowClasses: {
      type: [String, Array],
      default: () => ['row justify-content-center'],
    },
    dataBootstrapColClasses: {
      type: [String, Array],
      default: () => ['col-12 col-md-8'],
    },
    dataFullscreen: {
      type: [Boolean, String],
      default: false,
    },
  },
  setup() {
    return {
      castPropToType,
      mdAndUp,
    };
  },
  data() {
    return {
      modal: false,
      themeVariant: null,
    };
  },
  computed: {
    bootstrapContainerClasses() {
      return this.dataBootstrapContainerClasses && !this.fullscreen
        ? this.castPropToType(this.dataBootstrapContainerClasses, 'array')
        : [];
    },
    bootstrapRowClasses() {
      return this.dataBootstrapRowClasses && !this.fullscreen
        ? this.castPropToType(this.dataBootstrapRowClasses, 'array')
        : [];
    },
    bootstrapColClasses() {
      return this.dataBootstrapColClasses && !this.fullscreen
        ? this.castPropToType(this.dataBootstrapColClasses, 'array')
        : [];
    },
    fullscreen() {
      return this.dataFullscreen
        ? this.castPropToType(this.dataFullscreen, 'boolean')
        : null;
    },
  },
  mounted() {
    this.themeVariant = this.$el.classList.contains('highlighted')
      ? 'highlighted'
      : 'default';
  },
  methods: {
    onDismiss() {
      document.body.classList.remove('modal-open');
      this.modal = false;
    },
    trapFocus(el) {
      const focusableElements = this.$el
        .querySelector('.lightbox')
        .querySelectorAll(
          'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
        );

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      this.$el.addEventListener('keydown', (e) => {
        const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
        if (e.key === 'Escape') {
          this.onDismiss();
          return;
        }
        if (!isTabPressed) {
          return;
        }
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      });

      if (!el) {
        firstFocusableElement.focus();
      } else {
        el.focus();
      }
    },
    handlePlayerReady() {
      this.$nextTick(() => {
        this.trapFocus();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.MediaPlayerCmp {
  @include media-breakpoint-up(md) {
    --lightbox-overlay-outline: calc(
      #{$size-outline} + #{$size-outline-offset}
    );
  }

  ::v-deep .LightboxCmp {
    .zoom-out {
      position: absolute;
      top: 24px;
      right: 24px;
      z-index: map_get($z, above);

      svg {
        width: 32px;
        height: 32px;
        color: #ffffff;
      }
    }
  }
}
</style>
